<!-- src/components/stock/StockDeliveryNoteModal.vue -->

<template>
  <div ref="modalElement" class="modal delivery-note-modal">
    <div class="modal-content">
      <div class="modal-heading-row">
        <div>
          <h5 class="modal-heading">New Delivery Note</h5>

          <p class="grey-text text-darken-1 modal-subtitle">
            Enter the quantity delivered in Order Units. The resulting Stock In quantity
            is calculated from the Stock Database conversion.
          </p>
        </div>

        <button
          class="btn-flat modal-x-button"
          type="button"
          aria-label="Close"
          @click="close"
        >
          <i class="material-icons"> close </i>
        </button>
      </div>

      <div v-if="error" class="error-box">
        {{ error }}
      </div>

      <div v-if="loading" class="progress">
        <div class="indeterminate"></div>
      </div>

      <form v-else id="stock-delivery-note-form" @submit.prevent="saveDelivery">
        <div class="delivery-header-grid">
          <div class="form-field">
            <label for="stock-delivery-date"> Delivery Date </label>

            <input
              id="stock-delivery-date"
              v-model="deliveryDate"
              type="date"
              class="form-input"
              required
            />
          </div>

          <div class="form-field">
            <label for="stock-delivery-order"> Delivery Note Number </label>

            <select
              id="stock-delivery-order"
              v-model="selectedOrderId"
              class="browser-default form-select"
              required
              @change="orderChanged"
            >
              <option value="" disabled>Select Active Order</option>

              <option v-for="order in activeOrders" :key="order.id" :value="order.id">
                {{ order.orderReference || order.id }}
                —
                {{ order.orderDetails?.venueName || "Active venue" }}
                —
                {{ order.supplier || "Supplier" }}
              </option>
            </select>
          </div>
        </div>

        <div v-if="!activeOrders.length" class="empty-orders">
          <i class="material-icons"> inventory_2 </i>

          <div>
            <strong> No approved orders are awaiting delivery. </strong>

            <span>
              An Operations user must approve an order before its delivery note can be
              entered.
            </span>
          </div>
        </div>

        <template v-else-if="selectedOrder">
          <div class="selected-order-summary">
            <div>
              <span> Order Number </span>

              <strong>
                {{ selectedOrder.orderReference || selectedOrder.id }}
              </strong>
            </div>

            <div>
              <span> Supplier </span>

              <strong>
                {{ selectedOrder.supplier || "—" }}
              </strong>
            </div>

            <div>
              <span> Ordered Units </span>

              <strong>
                {{ formatNumber(selectedOrder.totals?.totalQuantity) }}
              </strong>
            </div>
          </div>

          <div class="delivery-lines-wrap">
            <table class="striped highlight delivery-lines-table">
              <thead>
                <tr>
                  <th>Product Name</th>

                  <th>Supplier</th>

                  <th>Order Unit</th>

                  <th class="right-align">Qty Ordered</th>

                  <th class="right-align delivered-heading">Qty Delivered</th>

                  <th class="right-align">Stock In</th>

                  <th class="right-align">Variance</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="line in lines" :key="line.product_id">
                  <td class="product-name">
                    {{ line.product_name }}
                  </td>

                  <td>
                    {{ line.supplier || selectedOrder.supplier || "—" }}
                  </td>

                  <td>
                    {{ line.order_unit || "—" }}
                  </td>

                  <td class="right-align">
                    {{ formatNumber(line.quantity_ordered) }}
                  </td>

                  <td class="right-align delivered-cell">
                    <input
                      v-model.number="line.quantity_delivered"
                      type="number"
                      min="0"
                      step="0.01"
                      inputmode="decimal"
                      class="quantity-input"
                      :aria-label="`Quantity delivered for ${line.product_name}`"
                      required
                    />
                  </td>

                  <td class="right-align stock-in-cell">
                    <strong>
                      {{ formatNumber(deliveredStockUnits(line)) }}
                    </strong>

                    {{ line.stock_unit }}
                  </td>

                  <td class="right-align variance-cell" :class="varianceClass(line)">
                    <span v-if="variance(line) !== 0">
                      {{ signedNumber(variance(line)) }}
                    </span>

                    <span v-else class="no-variance"> — </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="hasVariance" class="variance-notice">
            <i class="material-icons"> warning_amber </i>

            Only products where the delivered Order Unit quantity differs from the ordered
            quantity show a delivery variance.
          </div>
        </template>

        <div v-else-if="activeOrders.length" class="empty-selection">
          Select an approved order number to populate the delivery products.
        </div>
      </form>
    </div>

    <div class="modal-footer delivery-modal-footer">
      <button
        class="btn-flat waves-effect"
        type="button"
        :disabled="saving"
        @click="close"
      >
        Cancel
      </button>

      <button
        class="btn teal darken-2 waves-effect waves-light"
        type="submit"
        form="stock-delivery-note-form"
        :disabled="saving || !deliveryValid"
      >
        <i class="material-icons left"> save </i>

        {{ saving ? "Saving..." : "Save Delivery Note" }}
      </button>
    </div>
  </div>
</template>

<script>
import {
  fetchActiveDeliveryOrders,
  saveApprovedStockDeliveryNote,
} from "@/scripts/sqlApiHelper/stockCountApi";

import { fetchStockUnits } from "@/scripts/sqlApiHelper/stockUnitsApi";

function today() {
  const currentDate = new Date();

  const localDate = new Date(
    currentDate.getTime() - currentDate.getTimezoneOffset() * 60000
  );

  return localDate.toISOString().slice(0, 10);
}

export default {
  name: "StockDeliveryNoteModal",

  emits: ["saved"],

  props: {
    venueId: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      modalInstance: null,

      activeOrders: [],

      stockItems: [],

      selectedOrderId: "",

      deliveryDate: today(),

      lines: [],

      loading: false,

      saving: false,

      error: null,
    };
  },

  computed: {
    selectedOrder() {
      return this.activeOrders.find((order) => order.id === this.selectedOrderId) || null;
    },

    hasVariance() {
      return this.lines.some((line) => this.variance(line) !== 0);
    },

    deliveryValid() {
      return Boolean(
        this.deliveryDate &&
          this.selectedOrderId &&
          this.lines.length &&
          this.lines.every((line) => {
            const quantity = Number(line.quantity_delivered);

            return (
              Number.isFinite(quantity) &&
              quantity >= 0 &&
              this.number(line.stock_units_per_order_unit) > 0
            );
          })
      );
    },
  },

  mounted() {
    this.initModal();
  },

  beforeUnmount() {
    this.modalInstance?.destroy?.();

    this.modalInstance = null;
  },

  methods: {
    number(value) {
      const number = Number(value);

      return Number.isFinite(number) ? number : 0;
    },

    initModal() {
      if (!window.M || !this.$refs.modalElement) {
        return;
      }

      this.modalInstance = window.M.Modal.init(this.$refs.modalElement, {
        dismissible: true,

        opacity: 0.5,

        inDuration: 150,

        outDuration: 120,

        onCloseEnd: () => {
          this.resetForm();
        },
      });
    },

    async open() {
      if (!this.venueId) {
        this.error = "No active venue is selected.";

        return;
      }

      this.resetForm();

      this.loading = true;

      this.modalInstance?.open?.();

      try {
        const [orders, stockItems] = await Promise.all([
          fetchActiveDeliveryOrders(this.venueId),

          fetchStockUnits(),
        ]);

        this.activeOrders = Array.isArray(orders) ? orders : [];

        this.stockItems = Array.isArray(stockItems) ? stockItems : [];
      } catch (error) {
        console.error("[DELIVERY NOTE MODAL] Load failed:", error);

        this.error = error?.message || "Unable to load approved orders.";
      } finally {
        this.loading = false;
      }
    },

    close() {
      this.modalInstance?.close?.();
    },

    orderChanged() {
      const order = this.selectedOrder;

      if (!order) {
        this.lines = [];

        return;
      }

      const orderItems = Array.isArray(order.items) ? order.items : [];

      this.lines = orderItems.map((item) => {
        const productId = String(item.id || "").trim();

        const stockItem = this.stockItems.find((record) => record.id === productId);

        const stockPerOrder =
          this.number(item.stockUnitsPerOrderUnit) ||
          this.number(item.stockUnits) ||
          this.number(stockItem?.stock_units_per_order_unit);

        return {
          product_id: productId,

          product_name: String(item.product || "").trim(),

          supplier: String(item.supplier || order.supplier || "").trim(),

          order_unit: String(
            item.orderUnit || item.unitSize || stockItem?.order_unit || ""
          ).trim(),

          stock_unit: String(item.stockUnit || stockItem?.stock_unit || "").trim(),

          stock_units_per_order_unit: stockPerOrder,

          quantity_ordered: this.number(item.quantity),

          quantity_delivered: this.number(item.quantity),
        };
      });
    },

    deliveredStockUnits(line) {
      return (
        this.number(line.quantity_delivered) *
        this.number(line.stock_units_per_order_unit)
      );
    },

    variance(line) {
      return this.number(line.quantity_delivered) - this.number(line.quantity_ordered);
    },

    varianceClass(line) {
      const difference = this.variance(line);

      if (difference < 0) {
        return "variance-negative";
      }

      if (difference > 0) {
        return "variance-positive";
      }

      return "";
    },

    signedNumber(value) {
      const number = this.number(value);

      return `${number > 0 ? "+" : ""}` + `${this.formatNumber(number)}`;
    },

    async saveDelivery() {
      if (!this.deliveryValid || this.saving) {
        return;
      }

      this.saving = true;
      this.error = null;

      try {
        const result = await saveApprovedStockDeliveryNote({
          venueId: this.venueId,

          orderId: this.selectedOrderId,

          deliveryDate: this.deliveryDate,

          deliveries: this.lines.map((line) => ({
            itemId: line.product_id,

            quantityDelivered: this.number(line.quantity_delivered),
          })),
        });

        this.$emit("saved", result);

        window.M?.toast?.({
          html: "Delivery note saved successfully.",

          classes: "green darken-2",
        });

        this.close();
      } catch (error) {
        console.error("[DELIVERY NOTE MODAL] Save failed:", error);

        this.error =
          error?.details?.message || error?.message || "Unable to save delivery note.";
      } finally {
        this.saving = false;
      }
    },

    resetForm() {
      this.activeOrders = [];
      this.stockItems = [];

      this.selectedOrderId = "";

      this.deliveryDate = today();

      this.lines = [];

      this.error = null;
      this.saving = false;
      this.loading = false;
    },

    formatNumber(value) {
      return this.number(value).toLocaleString("en-GB", {
        maximumFractionDigits: 4,
      });
    },
  },
};
</script>

<style scoped>
.delivery-note-modal {
  width: min(1100px, 94vw);
  max-height: 90vh;
  border-radius: 12px;
  overflow: hidden;
}

.modal-content {
  max-height: calc(90vh - 72px);
  padding: 24px;
  overflow-y: auto;
}

.modal-heading-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.modal-heading,
.modal-subtitle {
  margin: 0;
}

.modal-subtitle {
  margin-top: 5px;
}

.modal-x-button {
  min-width: 42px;
  padding: 0 8px !important;
}

.error-box {
  margin-bottom: 16px;
  padding: 12px 14px;
  border: 1px solid #ef9a9a;
  border-radius: 8px;
  background: #ffebee;
  color: #b71c1c;
  font-weight: 600;
}

.delivery-header-grid {
  display: grid;
  grid-template-columns:
    minmax(180px, 0.65fr)
    minmax(300px, 1.35fr);
  gap: 18px;
  margin-bottom: 22px;
}

.form-field label {
  display: block;
  margin-bottom: 6px;
  color: #455a64;
  font-size: 0.84rem;
  font-weight: 600;
}

.form-input,
.form-select {
  box-sizing: border-box !important;
  width: 100% !important;
  height: 42px !important;
  margin: 0 !important;
  padding: 0 10px !important;
  border: 1px solid #bdbdbd !important;
  border-radius: 5px !important;
  background: #ffffff;
  box-shadow: none !important;
}

.form-input:focus,
.form-select:focus {
  border-color: #00897b !important;
  box-shadow: 0 0 0 1px #00897b !important;
  outline: none;
}

.empty-orders,
.empty-selection {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  min-height: 150px;
  padding: 24px;
  border: 1px dashed #b0bec5;
  border-radius: 10px;
  background: #fafafa;
  color: #607d8b;
  text-align: center;
}

.empty-orders i {
  color: #90a4ae;
  font-size: 32px;
}

.empty-orders strong,
.empty-orders span {
  display: block;
}

.empty-orders span {
  margin-top: 5px;
}

.selected-order-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.selected-order-summary > div {
  padding: 13px 14px;
  border: 1px solid #d7e7e4;
  border-radius: 8px;
  background: #f5fbfa;
}

.selected-order-summary span,
.selected-order-summary strong {
  display: block;
}

.selected-order-summary span {
  margin-bottom: 4px;
  color: #607d8b;
  font-size: 0.78rem;
}

.selected-order-summary strong {
  color: #004d40;
}

.delivery-lines-wrap {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.delivery-lines-table {
  min-width: 940px;
}

.delivery-lines-table th {
  color: #455a64;
  font-weight: 600;
  white-space: nowrap;
}

.delivery-lines-table td {
  vertical-align: middle;
}

.product-name {
  min-width: 220px;
  font-weight: 600;
}

.delivered-heading,
.delivered-cell {
  min-width: 145px;
}

.quantity-input {
  box-sizing: border-box !important;
  width: 100px !important;
  height: 36px !important;
  margin: 0 0 0 auto !important;
  padding: 0 8px !important;
  border: 1px solid #bdbdbd !important;
  border-radius: 5px !important;
  text-align: right;
}

.quantity-input:focus {
  border-color: #00897b !important;
  box-shadow: 0 0 0 1px #00897b !important;
}

.stock-in-cell {
  min-width: 110px;
  color: #00695c;
}

.variance-cell {
  font-weight: 700;
}

.variance-negative {
  color: #c62828;
}

.variance-positive {
  color: #ef6c00;
}

.no-variance {
  color: #9e9e9e;
  font-weight: 400;
}

.variance-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 15px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fff8e1;
  color: #e65100;
  font-size: 0.84rem;
}

.variance-notice i {
  font-size: 19px;
}

.delivery-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  min-height: 72px;
  padding: 12px 24px !important;
  border-top: 1px solid #eeeeee;
}

@media only screen and (max-width: 700px) {
  .delivery-note-modal {
    width: 96vw;
  }

  .modal-content {
    padding: 18px;
  }

  .delivery-header-grid,
  .selected-order-summary {
    grid-template-columns: 1fr;
  }

  .delivery-modal-footer {
    padding: 10px 16px !important;
  }
}
</style>
